import { Outlet, Link, useLocation } from 'react-router-dom';
import { useBffAuth } from '@/services/bffAuth';

export const Layout = () => {
  const location = useLocation();
  const { user, logout } = useBffAuth();

  const displayName = user?.name || user?.email || 'Admin';

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // PT-PT labels per Magni voice guidelines (sentence case, formal "você"
  // implied — verbless in nav).
  const navItems = [
    { path: '/', label: 'Painel', icon: 'dashboard' },
    { path: '/features', label: 'Funcionalidades', icon: 'star' },
    { path: '/metrics', label: 'Métricas', icon: 'analytics' },
    { path: '/clients', label: 'Clientes', icon: 'group' },
    { path: '/credits', label: 'Créditos', icon: 'account_balance_wallet' },
    { path: '/usage', label: 'Eventos', icon: 'history' },
  ];

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar — 280px on a gray-25 ground; single 1px divider on the
          right per Magni layout rules. */}
      <aside className="w-[280px] flex-shrink-0 bg-gray-25 flex flex-col border-r border-gray-200">
        {/* Canonical product wordmark per magni-design-system/project/preview/product-naming.html:
              [magn]   — Egon Sans Bold 700, magni-red
              [glass]  — magnifier symbol (magni-symbol.svg), magni-red, sized + positioned as a tall lowercase "i"
              [suffix] — Egon Sans ExtraLight Italic 200, gray #939598
            Same construction as magnifinance / magninbox / magnimoney — NOT 'magni' + 'usage'. */}
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <Link to="/" aria-label="MagniUsage — início" className="brand-wordmark-link select-none">
            <span className="brand-wm-magn">magn</span>
            <span className="brand-wm-glass" role="img" aria-label="i" />
            <span className="brand-wm-suffix">usage</span>
          </Link>
        </div>

        <nav className="px-4 py-6 flex-1">
          <p className="eyebrow px-4 mb-3" style={{ color: 'var(--color-fg-3)' }}>Navegar</p>
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-magni-red-50 text-magni-red font-semibold'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="px-6 py-4 border-t border-gray-200">
          <p className="text-xs text-gray-400">MagniUsage v1.0.0</p>
        </div>
      </aside>

      {/* Main content area. 64px top nav, white. The page H1 lives inside
          <main>, so the global header carries only user info — no
          duplicate page label. */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 flex-shrink-0 bg-white border-b border-gray-200 flex items-center justify-end px-6 gap-3">
          <span className="text-sm text-gray-600">{displayName}</span>
          <button
            onClick={() => logout()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">logout</span>
            Terminar sessão
          </button>
        </header>

        <main className="flex-1 overflow-y-auto bg-background-light">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
